import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Stack, Chip, IconButton, Tooltip, Drawer, Button, Divider, Avatar, Grid, CircularProgress,
} from '@mui/material';
import {
  Apple, Android, OpenInNew, Close as CloseIcon, ContentCopy,
  CheckCircle, ErrorOutline, HourglassEmpty, CloudDownload,
} from '@mui/icons-material';
import { playStoreUrlFor, resolveIosStoreUrls, ResolvedStoreUrl } from '../utils/storeUrls';
import { isAabDownloadable } from '../utils/aab';

// Normalised, component-agnostic shape so any table (App Deployment, etc.)
// can open the same per-app detail view without sharing its row type.
export interface AppDetails {
  appName: string;
  composerHost: string;
  composerAppId: string | number;
  platform: string; // 'ios' | 'android' | other
  version: string;
  sitekey?: string | null;
  bundleIdentifier?: string | null;
  status?: string;
  user?: string;
  updatedAt?: string;
  appIconUrl?: string;
  pipelineId?: string;
  aabUrl?: string | null;
  /** Epoch ms when the AAB finished building; used to hide expired links (2h TTL). */
  aabCompletedAt?: number | null;
  raw?: any;
}

// Resolve a normalised AppDetails from the dashboard appData map. Prefers a
// stable composerAppId (optionally scoped by platform), then host + platform,
// then host alone — so dual-platform / duplicate-host apps map to the right one.
export function resolveAppDetails(
  appData: any,
  host: string,
  platformParam?: string,
  appIdParam?: string | number,
): AppDetails | null {
  if (!host || !appData) return null;
  const target = host.toLowerCase();
  const plat = (platformParam ?? '').toString().trim().toLowerCase();
  const wantId = (appIdParam ?? '').toString().trim();
  const entries = Object.values(appData as Record<string, any>);
  const hostOf = (a: any) => (a.domain || a.composerHost || '').toLowerCase();
  const platformOf = (a: any) => (a.platform || '').toLowerCase();
  const appIdOf = (a: any) => String(a.composer_app_id ?? a.composerAppId ?? '');

  let entry: any = null;
  if (wantId) {
    entry = entries.find((a: any) => appIdOf(a) === wantId && (!plat || platformOf(a) === plat));
  }
  if (!entry && plat) {
    entry = entries.find((a: any) => hostOf(a) === target && platformOf(a) === plat);
  }
  if (!entry) {
    entry = entries.find((a: any) => hostOf(a) === target);
  }
  if (!entry) return null;

  const platform = (entry.platform || '').toLowerCase();
  const version = (platform === 'ios' ? entry.ios_version : entry.android_version) || entry.version || 'N/A';
  return {
    appName: entry.app_name || entry.appName || entry.domain || host,
    composerHost: entry.domain || entry.composerHost || host,
    composerAppId: entry.composer_app_id ?? entry.composerAppId ?? '—',
    platform,
    version,
    sitekey: entry.sitekey ?? null,
    bundleIdentifier: entry.bundleIdentifier ?? null,
    status: entry.status,
    user: entry.user,
    updatedAt: entry.updatedAt,
    appIconUrl: entry.app_icon_url,
    pipelineId: entry.pipelineId,
    aabUrl: entry.aabUrl,
    aabCompletedAt: entry.aabCompletedAt ?? entry.aab_completed_at ?? null,
    raw: entry,
  };
}

const statusMeta = (status: string) => {
  const s = (status || '').toLowerCase();
  if (/success/.test(s)) return { label: 'Success', color: '#16a34a', bg: 'rgba(22, 163, 74, 0.12)', icon: <CheckCircle sx={{ fontSize: 13 }} /> };
  if (/fail|cancel/.test(s)) return { label: /cancel/.test(s) ? 'Cancelled' : 'Failed', color: '#b91c1c', bg: 'rgba(220, 38, 38, 0.12)', icon: <ErrorOutline sx={{ fontSize: 13 }} /> };
  if (/queue|running|pending/.test(s)) return { label: 'Running', color: '#1d4ed8', bg: 'rgba(59, 130, 246, 0.12)', icon: <HourglassEmpty sx={{ fontSize: 13 }} /> };
  return { label: status || 'N/A', color: '#475569', bg: 'rgba(15, 23, 42, 0.06)', icon: null };
};

// The worker has stamped the bundle id under a few field names over time;
// prefer the normalised field, fall back to the raw payload's variants.
const bundleIdOf = (app: AppDetails): string =>
  app.bundleIdentifier
  || app.raw?.appdata?.bundleIdentifier
  || app.raw?.appdata?.bundle_identifier
  || app.raw?.bundleIdentifier
  || app.raw?.bundle_identifier
  || '';

const fmtDate = (s: unknown): string => {
  if (!s || typeof s !== 'string') return '—';
  const t = new Date(s).getTime();
  return Number.isFinite(t) ? new Date(t).toLocaleString() : s;
};

// The per-app detail body. Rendered inside the right-side drawer (from the App
// Deployment table) and standalone on the App Details page (from "view app
// details" links elsewhere). `onClose` powers the header X / footer button.
export const AppDetailsContent: React.FC<{ app: AppDetails; onClose?: () => void; closeLabel?: string }> = ({ app, onClose, closeLabel = 'Close' }) => {
  // iOS store URL. undefined = lookup in flight, null = no match / N/A.
  const [iosUrl, setIosUrl] = useState<ResolvedStoreUrl | null | undefined>(undefined);
  const [copyMsg, setCopyMsg] = useState<string | null>(null);

  const bundleId = bundleIdOf(app);
  const isIosApp = (app.platform || '').toLowerCase() === 'ios';

  useEffect(() => {
    setIosUrl(undefined);
    if (!isIosApp || !bundleId) return;
    let cancelled = false;
    resolveIosStoreUrls([bundleId])
      .then(resolved => { if (!cancelled) setIosUrl(resolved[bundleId] ?? null); })
      .catch(() => { if (!cancelled) setIosUrl(null); });
    return () => { cancelled = true; };
  }, [isIosApp, bundleId]);

  type StoreLinkState =
    | { kind: 'resolved'; target: ResolvedStoreUrl }
    | { kind: 'pending' }
    | { kind: 'unavailable' };
  const storeLink = useMemo<StoreLinkState>(() => {
    if (!bundleId) return { kind: 'unavailable' };
    const platform = (app.platform || '').toLowerCase();
    if (platform === 'android') {
      const target = playStoreUrlFor(bundleId);
      return target ? { kind: 'resolved', target } : { kind: 'unavailable' };
    }
    if (platform === 'ios') {
      if (iosUrl === undefined) return { kind: 'pending' };
      return iosUrl ? { kind: 'resolved', target: iosUrl } : { kind: 'unavailable' };
    }
    return { kind: 'unavailable' };
  }, [app, bundleId, iosUrl]);

  const copyToClipboard = (value: string) => {
    const flash = (msg: string) => { setCopyMsg(msg); setTimeout(() => setCopyMsg(null), 1500); };
    const p = navigator.clipboard?.writeText(value);
    if (!p) { flash('Copy failed'); return; }
    p.then(() => flash('Copied')).catch(() => flash('Copy failed'));
  };

  const r = app;
  const isIOS = (r.platform || '').toLowerCase() === 'ios';
  const isAndroid = (r.platform || '').toLowerCase() === 'android';
  const meta = statusMeta(r.status || 'N/A');
  const raw = r.raw || {};
  const appdata = (raw.appdata || {}) as Record<string, any>;
  const gitlabdata = (raw.gitlabdata || {}) as Record<string, any>;
  const triggerData = Array.isArray(raw.triggerData) ? raw.triggerData : [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar src={r.appIconUrl} variant="rounded" sx={{ width: 44, height: 44, bgcolor: isIOS ? '#0f172a' : '#3DDC84' }}>
            {isIOS ? <Apple sx={{ fontSize: 22 }} /> : <Android sx={{ fontSize: 22 }} />}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.15 }}>{r.appName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {r.composerHost} · v{r.version} · {isIOS ? 'iOS' : isAndroid ? 'Android' : r.platform || '—'}
            </Typography>
          </Box>
        </Stack>
        {onClose && <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>}
      </Box>

      <Divider />

      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
          <Chip
            size="small"
            icon={meta.icon || undefined}
            label={`Last build: ${meta.label}`}
            sx={{ fontWeight: 700, bgcolor: meta.bg, color: meta.color, '& .MuiChip-icon': { color: meta.color, ml: 0.5 } }}
          />
          <Chip
            size="small"
            icon={isIOS ? <Apple sx={{ fontSize: 14 }} /> : isAndroid ? <Android sx={{ fontSize: 14 }} /> : undefined}
            label={isIOS ? 'iOS' : isAndroid ? 'Android' : r.platform || 'Other'}
            sx={{ fontWeight: 700 }}
          />
        </Stack>

        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>App identity</Typography>
        <DetailGrid
          items={[
            { label: 'App name', value: r.appName },
            { label: 'Composer host', value: r.composerHost, breakAll: true },
            { label: 'Composer app ID', value: String(r.composerAppId), mono: true },
            { label: 'Platform', value: r.platform || '—' },
            { label: 'Version', value: `v${r.version}`, mono: true },
          ]}
          onCopy={copyToClipboard}
        />

        <Divider sx={{ my: 2 }} />
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>App configuration</Typography>
        <DetailGrid
          items={[
            { label: 'Site key', value: r.sitekey || appdata.sitekey || '—', mono: true, breakAll: true },
            { label: 'Bundle identifier', value: bundleId || '—', mono: true, breakAll: true },
            { label: 'OTA enabled', value: appdata.otaEnabled === undefined ? '—' : appdata.otaEnabled ? 'Yes' : 'No' },
            { label: 'App icon URL', value: r.appIconUrl || '—', link: r.appIconUrl, breakAll: true },
          ]}
          onCopy={copyToClipboard}
        />

        {/* Store listing — canonical Play Store / App Store URL. iOS goes
            through iTunes Lookup (cached 7 days) for the exact listing. */}
        {storeLink.kind !== 'unavailable' && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>Store listing</Typography>
            {storeLink.kind === 'pending' ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
                <CircularProgress size={18} sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Looking up App Store listing for <code style={{ fontFamily: 'monospace' }}>{bundleId}</code>…
                </Typography>
              </Box>
            ) : (
              <Box
                component="a"
                href={storeLink.target.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2,
                  textDecoration: 'none', border: '1px solid', borderColor: 'divider', color: 'text.primary',
                  transition: 'background-color 120ms ease, border-color 120ms ease',
                  '&:hover': {
                    bgcolor: isIOS ? 'rgba(15, 23, 42, 0.04)' : 'rgba(61, 220, 132, 0.08)',
                    borderColor: isIOS ? '#0f172a' : '#1b8a47',
                  },
                }}
              >
                <Box sx={{ width: 36, height: 36, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: isIOS ? '#0f172a' : '#1b8a47', color: '#fff', flexShrink: 0 }}>
                  {isIOS ? <Apple sx={{ fontSize: 18 }} /> : <Android sx={{ fontSize: 18 }} />}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    Open in {storeLink.target.label}
                    {storeLink.target.trackId ? <span style={{ fontWeight: 400, color: '#64748b', marginLeft: 8 }}>· id {storeLink.target.trackId}</span> : null}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                    {storeLink.target.url}
                  </Typography>
                </Box>
                <OpenInNew sx={{ fontSize: 18, color: 'text.secondary' }} />
              </Box>
            )}
          </>
        )}

        {Object.keys(gitlabdata).length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>GitLab pipeline</Typography>
            <DetailGrid
              items={[
                { label: 'Pipeline ID', value: gitlabdata.id ? String(gitlabdata.id) : '—', mono: true },
                { label: 'Status', value: gitlabdata.status || '—' },
                // Ref / branch and Web URL are only shown when GitLab actually
                // reported them — an empty "—" row is noise on queued builds.
                ...(gitlabdata.ref ? [{ label: 'Ref / branch', value: String(gitlabdata.ref), mono: true }] : []),
                ...(gitlabdata.web_url ? [{ label: 'Web URL', value: String(gitlabdata.web_url), link: gitlabdata.web_url, breakAll: true }] : []),
              ]}
              onCopy={copyToClipboard}
            />
          </>
        )}

        {triggerData.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>Trigger history</Typography>
              <Chip size="small" label={`${triggerData.length} trigger${triggerData.length !== 1 ? 's' : ''}`} sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: 'rgba(99, 102, 241, 0.1)', color: '#4338ca' }} />
            </Stack>
            <Stack spacing={1}>
              {triggerData.slice(0, 5).map((t: any, i: number) => (
                <Box key={i} sx={{ p: 1.25, border: '1px solid #e2e8f0', borderRadius: 1.5, fontSize: '0.78rem' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a' }}>{t.user || t.submitter || t.androidUser || t.iosUser || r.user || 'Unknown'}</Typography>
                    <Typography variant="caption" color="text.secondary">{fmtDate(t.end_time || t.updatedAt || t.androidUpdatedAt || t.iosUpdatedAt || t.timestamp || r.updatedAt)}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                    {t.version && <Chip size="small" label={`v${t.version}`} sx={{ height: 16, fontSize: '0.6rem', fontFamily: 'monospace' }} />}
                    {t.status && <Chip size="small" label={t.status} sx={{ height: 16, fontSize: '0.6rem' }} />}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>Latest build snapshot</Typography>
        <DetailGrid
          items={[
            { label: 'Status', value: r.status || 'N/A' },
            { label: 'Phase', value: raw.phase || '—' },
            { label: 'Pipeline ID', value: r.pipelineId ? String(r.pipelineId) : '—', mono: true },
            { label: 'Submitter', value: r.user || '—', breakAll: true },
            { label: 'Updated at', value: fmtDate(r.updatedAt) },
          ]}
          onCopy={copyToClipboard}
        />

        {(() => {
          // Only surface a still-valid link. Validity comes from the presigned
          // URL's own expiry (2h), with a completion-timestamp fallback.
          const aabFresh = isAabDownloadable(r.aabUrl, r.aabCompletedAt);
          if (!aabFresh) return null;
          return (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>AAB artifact</Typography>
              <Button
                variant="outlined"
                size="small"
                href={r.aabUrl!}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<CloudDownload sx={{ fontSize: 18 }} />}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Download AAB
              </Button>
            </>
          );
        })()}
      </Box>

      {onClose && (
        <>
          <Divider />
          <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={onClose} color="inherit">{closeLabel}</Button>
          </Box>
        </>
      )}

      {copyMsg && (
        <Box sx={{ position: 'fixed', bottom: 24, right: 24, bgcolor: '#0f172a', color: '#fff', px: 2, py: 1, borderRadius: 2, zIndex: 1400, fontSize: '0.85rem' }}>
          {copyMsg}
        </Box>
      )}
    </Box>
  );
};

interface AppDetailsDrawerProps {
  app: AppDetails | null;
  onClose: () => void;
}

export const AppDetailsDrawer: React.FC<AppDetailsDrawerProps> = ({ app, onClose }) => (
  <Drawer
    anchor="right"
    open={!!app}
    onClose={onClose}
    slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', backgroundColor: 'rgba(15, 23, 42, 0.3)' } } }}
    PaperProps={{ sx: { width: { xs: '100%', sm: 600 }, borderRadius: 0 } }}
  >
    {app && <AppDetailsContent app={app} onClose={onClose} />}
  </Drawer>
);

const DetailGrid: React.FC<{
  items: Array<{ label: string; value: string; mono?: boolean; breakAll?: boolean; link?: string }>;
  onCopy?: (value: string) => void;
}> = ({ items, onCopy }) => (
  <Grid container spacing={1.5}>
    {items.map((it) => (
      <Grid item xs={12} sm={6} key={it.label}>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.06em' }}>
          {it.label}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {it.link && it.value !== '—' ? (
            <Typography
              component="a" href={it.link} target="_blank" rel="noopener noreferrer"
              variant="body2"
              sx={{ fontWeight: 600, color: 'primary.main', textDecoration: 'none', fontFamily: it.mono ? 'monospace' : 'inherit', overflowWrap: it.breakAll ? 'anywhere' : 'normal' }}
            >
              {it.value}
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: it.value === '—' ? 'text.secondary' : '#0f172a', fontFamily: it.mono ? 'monospace' : 'inherit', overflowWrap: it.breakAll ? 'anywhere' : 'normal' }}
            >
              {it.value}
            </Typography>
          )}
          {onCopy && it.value !== '—' && (
            <Tooltip title="Copy" arrow>
              <IconButton size="small" onClick={() => onCopy(it.value)} sx={{ p: 0.25 }}>
                <ContentCopy sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Grid>
    ))}
  </Grid>
);

export default AppDetailsDrawer;
